import { baseApi } from "./baseApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

export type MemberTier = "wider_audience" | "inner_circle" | "legacy_fellows";

export type UserRole =
  | "mentee"
  | "regional_rep"
  | "portal_manager"
  | "committee_member"
  | "committee_chair"
  | "admin"
  | "co"
  | "cyp"
  | "ryp"
  | "mentor";

export interface AuthUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  role: UserRole;
  // GIMLP programme profile
  region?: string;
  province?: string;
  interests?: string[];
  ministryRole?: string;
  tier: MemberTier;
  programmeStartQuarter?: "Q1" | "Q2" | "Q3" | "Q4";
  fellowsEligible: boolean;
  fellowsEligibleQuarter?: "Q1" | "Q2" | "Q3" | "Q4" | null;
  // Auth state
  isEmailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  hasCompletedOnboarding: boolean;
  hasSeenWelcome: boolean;
}

export interface AuthRegion {
  id: string;
  name: string;
  address: string;
}

export interface RegionsResponse {
  success: boolean;
  message: string;
  count: number;
  data: AuthRegion[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  // Optional GIMLP fields collected at registration
  region?: string;
  province?: string;
  ministryRole?: string;
  programmeStartQuarter?: "Q1" | "Q2" | "Q3" | "Q4";
}

export interface AuthResponse {
  success: boolean;
  message: string;
  user: AuthUser;
  token: string;
}

interface MessageResponse {
  success: boolean;
  message: string;
  data: null;
}

interface UserResponse {
  success: boolean;
  message: string;
  data: AuthUser;
}

/* ─────────────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────────────── */

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: "/auth/login",
        method: "POST",
        body: credentials,
      }),
    }),

    register: builder.mutation<AuthResponse, RegisterRequest>({
      query: (data) => ({
        url: "/auth/register",
        method: "POST",
        body: data,
      }),
    }),

    getAvailableRegions: builder.query<RegionsResponse, void>({
      query: () => "/auth/regions",
    }),

    logout: builder.mutation<MessageResponse, void>({
      query: () => ({ url: "/auth/logout", method: "POST" }),
    }),

    getCurrentUser: builder.query<UserResponse, void>({
      query: () => "/auth/me",
      providesTags: [{ type: "User", id: "CURRENT" }],
    }),

    updateProfile: builder.mutation<
      UserResponse,
      Partial<
        Pick<
          AuthUser,
          | "firstName"
          | "lastName"
          | "avatar"
          | "region"
          | "province"
          | "ministryRole"
        >
      >
    >({
      query: (data) => ({
        url: "/auth/profile",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [{ type: "User", id: "CURRENT" }],
    }),

    changePassword: builder.mutation<
      MessageResponse,
      { currentPassword: string; newPassword: string }
    >({
      query: (data) => ({
        url: "/auth/change-password",
        method: "POST",
        body: data,
      }),
    }),

    forgotPassword: builder.mutation<MessageResponse, { email: string }>({
      query: (data) => ({
        url: "/auth/forgot-password",
        method: "POST",
        body: data,
      }),
    }),

    resetPassword: builder.mutation<
      MessageResponse,
      { token: string; password: string }
    >({
      query: (data) => ({
        url: "/auth/reset-password",
        method: "POST",
        body: data,
      }),
    }),

    verifyEmail: builder.mutation<MessageResponse, { token: string }>({
      query: ({ token }) => ({
        url: `/auth/verify-email?token=${token}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useGetAvailableRegionsQuery,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyEmailMutation,
} = authApi;